import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import { useState, useEffect } from "react";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    const checkScrollTop = () => {
      if (!showScroll && window.pageYOffset > 400) {
        setShowScroll(true);
      } else if (showScroll && window.pageYOffset <= 400) {
        setShowScroll(false);
      }
    };

    window.addEventListener("scroll", checkScrollTop);
    return () => window.removeEventListener("scroll", checkScrollTop);
  }, [showScroll]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  return (
    <>
      {/* Urgent CTA Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          py: { xs: 6, md: 8 },
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
        id="contact-form"
      >
        {/* Animated Background Elements */}
        <motion.div
          style={{
            position: "absolute",
            top: "20%",
            left: "10%",
            width: 80,
            height: 80,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
          }}
          animate={floatingAnimation}
        />
        <motion.div
          style={{
            position: "absolute",
            bottom: "30%",
            right: "15%",
            width: 60,
            height: 60,
            borderRadius: "30%",
            background: "rgba(255,255,255,0.08)",
            rotate: 45,
          }}
          animate={{
            ...floatingAnimation,
            transition: { ...floatingAnimation.transition, delay: 1.5 },
          }}
        />

        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h3"
              sx={{
                color: "white",
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: "2rem", md: "2.5rem" },
              }}
            >
              🚀 Need Urgent Cleaning?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.9)",
                mb: 4,
                fontSize: { xs: "1.1rem", md: "1.25rem" },
              }}
            >
              Get instant service — Call us now!
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 3,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link href="tel:0752058190" underline="none">
                  <Button
                    variant="contained"
                    startIcon={<PhoneIcon />}
                    sx={{
                      backgroundColor: "white",
                      color: "primary.main",
                      px: 4,
                      py: 2,
                      borderRadius: 3,
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      boxShadow: "0 8px 25px rgba(255,255,255,0.3)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.9)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 35px rgba(255,255,255,0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    0752 058 190
                  </Button>
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  href="https://wa.me/256752058190"
                  target="_blank"
                  underline="none"
                >
                  <Button
                    variant="outlined"
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      borderColor: "white",
                      borderWidth: 2,
                      color: "white",
                      px: 4,
                      py: 2,
                      borderRadius: 3,
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      backdropFilter: "blur(10px)",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.15)",
                        borderColor: "white",
                        transform: "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    WhatsApp Us
                  </Button>
                </Link>
              </motion.div>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Main Footer */}
      <Box
        component="footer"
        sx={{
          background: "linear-gradient(135deg, #22335F 0%, #152347 100%)",
          color: "white",
          pt: { xs: 6, md: 8 },
          pb: 3,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 20% 80%, rgba(255,255,255,0.03) 0%, transparent 50%),
                        radial-gradient(circle at 80% 20%, rgba(255,255,255,0.02) 0%, transparent 50%)`,
          }}
        />

        <Container sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid item xs={12} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CleaningServicesIcon
                      sx={{ color: "white", fontSize: 28 }}
                    />
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "white",
                      background:
                        "linear-gradient(45deg, #FFFFFF 30%, #E3F2FD 90%)",
                      backgroundClip: "text",
                      WebkitBackgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    Blossom City Cleaning
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 3,
                    color: "rgba(255,255,255,0.8)",
                    lineHeight: 1.7,
                    fontSize: "1rem",
                  }}
                >
                  Professional home and commercial cleaning services in Kampala.
                  Trusted, insured, and eco-friendly solutions for your space.
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {[
                    {
                      icon: <FacebookIcon />,
                      color: "#1877F2",
                      label: "Facebook",
                    },
                    {
                      icon: <InstagramIcon />,
                      color: "#E4405F",
                      label: "Instagram",
                    },
                    {
                      icon: <TwitterIcon />,
                      color: "#1DA1F2",
                      label: "Twitter",
                    },
                    {
                      icon: <WhatsAppIcon />,
                      color: "#25D366",
                      label: "WhatsApp",
                    },
                  ].map((social, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.2, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <IconButton
                        sx={{
                          color: "white",
                          background: `rgba(255,255,255,0.1)`,
                          backdropFilter: "blur(10px)",
                          "&:hover": {
                            background: social.color,
                            transform: "translateY(-2px)",
                          },
                          transition: "all 0.3s ease",
                        }}
                        aria-label={social.label}
                      >
                        {social.icon}
                      </IconButton>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Quick Links */}
            <Grid item xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    color: "white",
                    fontSize: "1.25rem",
                  }}
                >
                  Quick Links
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {[
                    { name: "Home", path: "/" },
                    { name: "About Us", path: "/about" },
                    { name: "Services", path: "/services" },
                    { name: "Contact Us", path: "/contact" },
                  ].map((link, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link
                        component={RouterLink}
                        to={link.path}
                        sx={{
                          color: "rgba(255,255,255,0.8)",
                          textDecoration: "none",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          "&:hover": {
                            color: "white",
                            transform: "translateX(5px)",
                          },
                          transition: "all 0.3s ease",
                          "&::before": {
                            content: '"▸"',
                            color: "primary.main",
                            fontSize: "1.2rem",
                          },
                        }}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Contact Info */}
            <Grid item xs={12} sm={6} md={4}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 3,
                    color: "white",
                    fontSize: "1.25rem",
                  }}
                >
                  Contact Info
                </Typography>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #4CAF50, #45a049)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PhoneIcon sx={{ fontSize: 20, color: "white" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 500 }}
                      >
                        Call Us
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "white", fontWeight: 600 }}
                      >
                        0752 058 190
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "white", fontWeight: 600 }}
                      >
                        0200 940 830
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #2196F3, #1976D2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <EmailIcon sx={{ fontSize: 20, color: "white" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 500 }}
                      >
                        Email Us
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.9rem",
                        }}
                      >
                        bcitycleaningservices@gmail.com
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, #FF6B35, #F7931E)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <LocationOnIcon sx={{ fontSize: 20, color: "white" }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.8)", fontWeight: 500 }}
                      >
                        Visit Us
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.9rem",
                        }}
                      >
                        Kololo — Opposite Libya Embassy, Kampala
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Divider
              sx={{
                my: 4,
                backgroundColor: "rgba(255,255,255,0.2)",
                height: 2,
                borderRadius: 2,
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  fontWeight: 500,
                }}
              >
                © {new Date().getFullYear()} Blossom Cleaning Services. All
                rights reserved.
              </Typography>
              <Box sx={{ display: "flex", gap: 3 }}>
                {["Privacy Policy", "Terms of Service"].map((item, index) => (
                  <Link
                    key={index}
                    href="#"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontWeight: 500,
                      textDecoration: "none",
                      "&:hover": {
                        color: "white",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {item}
                  </Link>
                ))}
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Scroll to Top Button */}
      <AnimatePresence>
        {showScroll && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.3 }}
          >
            <IconButton
              onClick={scrollToTop}
              sx={{
                position: "fixed",
                bottom: 110,
                right: 30,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                width: 40,
                height: 40,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                  transform: "translateY(-3px)",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
                },
                zIndex: 1000,
                transition: "all 0.3s ease",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
              }}
              aria-label="Scroll to top"
            >
              <ArrowUpwardIcon />
            </IconButton>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Footer;
